import { useState, useEffect } from 'react'

export interface BookSearchResult {
  title: string
  author: string
  isbn?: string
  asin?: string
  bookshop_slug?: string
  cover_url?: string
  first_publish_year?: number
  number_of_pages?: number
  description?: string
  language?: string
  metadata?: any
}

interface UseBookSearchOptions {
  minQueryLength?: number
  debounceMs?: number
}

export function useBookSearch(options: UseBookSearchOptions = {}) {
  const { minQueryLength = 4, debounceMs = 700 } = options

  const [searchQuery, setSearchQuery] = useState('')
  const [results, setResults] = useState<BookSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (searchQuery.length < minQueryLength) {
      setResults([])
      return
    }

    const timer = setTimeout(() => {
      searchBooks()
    }, debounceMs)

    return () => clearTimeout(timer)
  }, [searchQuery, minQueryLength, debounceMs])

  const searchBooks = async () => {
    setLoading(true)
    setError('')

    try {
      const response = await fetch(`/api/v1/book_search?q=${encodeURIComponent(searchQuery)}`)
      if (response.ok) {
        const data = await response.json()
        setResults(data.results || [])
      } else {
        setError('Failed to search books')
        setResults([])
      }
    } catch (err) {
      setError('Failed to search books')
      setResults([])
    } finally {
      setLoading(false)
    }
  }

  const clearSearch = () => {
    setSearchQuery('')
    setResults([])
    setError('')
  }

  return {
    searchQuery,
    setSearchQuery,
    results,
    loading,
    error,
    clearSearch
  }
}
